import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useSessionManager } from '../SessionManager'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import GuacamoleConsole from '../GuacamoleConsole'
import ReconnectionDialog from '../ReconnectionDialog'
import ClipboardPanel, {
  useClipboardSync,
  clipboardApiAvailable,
  clampClipboard,
} from '../ClipboardPanel'
import { GLOBALS } from '../../../utils/Globals'
import { cycleLoadPDU, setStateLoadPDU, showStateLoadPDU } from '../../../services/PDU'
import { Context } from '../../../Context'
import { Dialog } from 'primereact/dialog'

import {
  AiFillWindows,
  AiOutlineCluster,
  AiFillCheckCircle,
  AiFillCloseCircle,
} from 'react-icons/ai'
import { MdDevices } from 'react-icons/md'
import { FcLinux } from 'react-icons/fc'
import 'react-tabs/style/react-tabs.css'
import { Button } from 'primereact/button'
import './styles.scss'

const MultiSessionManager = ({ ResoucesTokens, topologyTab, guideTab }) => {
  const [vmIds, setVmIds] = useState([]) // Almacena las VM IDs conectadas
  const { isAuth: token } = useContext(Context)
  const {
    startSession,
    getSession,
    errorMessage,
    disconnectClient,
    sendClipboard,
    getRemoteClipboard,
  } = useSessionManager()
  const [clipboardVmId, setClipboardVmId] = useState(null) // VM con el panel de portapapeles abierto
  const lastSyncRef = React.useRef({}) // vmId -> último envío por clic (límite de 3 s)
  const [selectedIndex, setSelectedIndex] = useState(0) // Índice de la pestaña seleccionada
  const [showMenu, setShowMenu] = useState(false)
  const [displayStatus, setDisplayStatus] = useState(true)
  const [isLoading] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false) // Estado para el modo pantalla completa
  const consoleRef = React.useRef(null) // Referencia a la consola para pantalla completa
  const [isPowerOffLoading, setIsPowerOffLoading] = useState(false) // Estado para el modal de loading
  const [powerOffVmName, setPowerOffVmName] = useState('') // Nombre de la VM que se está reiniciando
  const [, setVmPowerStates] = useState({}) // Estado de encendido/apagado de cada VM
  const [pduStates, setPduStates] = useState({}) // Estado real de la PDU para cada VM
  const [reconnectionDialog, setReconnectionDialog] = useState({
    visible: false,
    vmId: null,
    displayName: '',
    isConnected: false,
  })

  useEffect(() => {
    if (ResoucesTokens) {
      // Convertir el objeto a un array de [vmId, {token, displayName, resourceType}]
      const resourcesArray = Object.entries(ResoucesTokens)

      // Filtrar para evitar añadir duplicados a vmIds
      const newVmIds = resourcesArray.filter(([vmId]) => {
        return !vmIds.some((vm) => vm.vmId === vmId)
      })

      // Si hay nuevas VMs, las agregamos y empezamos las sesiones
      if (newVmIds.length > 0) {
        newVmIds.forEach(([vmId, resource]) => {
          startSession(vmId, resource.token, resource, token) // Iniciar sesión con vmId, token, resource y authToken
        })

        // vmIds solo una vez para evitar duplicaciones
        setVmIds((prev) => [
          ...prev,
          ...newVmIds.map(([vmId, resource]) => ({
            vmId,
            ...resource,
          })),
        ])

        // Inicializar estados de encendido/apagado para nuevas VMs
        const newPowerStates = {}
        const newPduStates = {}
        newVmIds.forEach(([vmId]) => {
          newPowerStates[vmId] = 'on' // Por defecto asumimos que están encendidas
          newPduStates[vmId] = 'unknown' // Estado inicial desconocido
        })
        setVmPowerStates((prev) => ({ ...prev, ...newPowerStates }))
        setPduStates((prev) => ({ ...prev, ...newPduStates }))
      }
    }
  }, [ResoucesTokens, startSession, vmIds])

  // Portapapeles automático de la VM de la pestaña activa
  const vmTabIndex = selectedIndex - (guideTab ? 1 : 0) - (topologyTab ? 1 : 0)
  const activeVmId = vmTabIndex >= 0 && vmTabIndex < vmIds.length ? vmIds[vmTabIndex].vmId : null
  useClipboardSync({
    vmId: activeVmId,
    active: activeVmId !== null,
    connected: activeVmId !== null && !!getSession(activeVmId).isConnected,
    remoteText: activeVmId !== null ? getRemoteClipboard(activeVmId) : '',
    send: (text) => (activeVmId !== null ? sendClipboard(activeVmId, text) : false),
  })

  // Al salir del laboratorio (desmontaje) cerrar las conexiones de estas VMs
  const vmIdsRef = React.useRef(vmIds)
  vmIdsRef.current = vmIds
  const disconnectClientRef = React.useRef(disconnectClient)
  disconnectClientRef.current = disconnectClient
  useEffect(() => {
    return () => {
      vmIdsRef.current.forEach(({ vmId }) => disconnectClientRef.current(vmId))
    }
  }, [])

  // Función para obtener el estado de la PDU
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPduState = async (vmId, resource) => {
    try {
      // Validar que el resource y sus propiedades existan
      if (!resource || !resource.host || resource.load === undefined || resource.load === null) {
        setPduStates((prev) => ({
          ...prev,
          [vmId]: 'error',
        }))
        return
      }

      const response = await showStateLoadPDU(
        {
          host: resource.host,
          username: 'ElviraAdmin',
          password: '3lvira_:D',
          port: 22,
          load: resource.load,
        },
        token
      )

      // Actualizar el estado de la PDU
      const newPduState = response.status || 'unknown'
      setPduStates((prev) => ({
        ...prev,
        [vmId]: newPduState, // Usar el campo 'status' de la respuesta
      }))

      // Sincronizar vmPowerStates con pduStates
      setVmPowerStates((prev) => ({
        ...prev,
        [vmId]: newPduState,
      }))
    } catch (error) {
      console.error(`Error al obtener estado PDU para VM ${vmId}:`, error)
      setPduStates((prev) => ({
        ...prev,
        [vmId]: 'error',
      }))

      // Sincronizar vmPowerStates con pduStates en caso de error
      setVmPowerStates((prev) => ({
        ...prev,
        [vmId]: 'error',
      }))
    }
  }

  // useEffect para polling cada 10 segundos
  useEffect(() => {
    if (vmIds.length === 0) return

    const interval = setInterval(() => {
      vmIds.forEach(({ vmId }) => {
        const resource = ResoucesTokens[vmId]
        if (resource) {
          fetchPduState(vmId, resource)
        }
      })
    }, 10000) // Cada 10 segundos

    // Limpiar el intervalo cuando el componente se desmonte o cambien las VMs
    return () => clearInterval(interval)
  }, [vmIds, ResoucesTokens, token, fetchPduState])

  // Efecto para monitorear el estado de conexión del diálogo de reconexión
  useEffect(() => {
    if (reconnectionDialog.visible && reconnectionDialog.vmId && !reconnectionDialog.isConnected) {
      const session = getSession(reconnectionDialog.vmId)

      if (session && session.isConnected) {
        // Esperar un poco más para que la consola se renderice
        const timer = setTimeout(() => {
          setReconnectionDialog((prev) => ({
            ...prev,
            isConnected: true,
          }))
        }, 2000) // 2 segundos de espera

        return () => clearTimeout(timer)
      }
    }
  }, [
    reconnectionDialog.visible,
    reconnectionDialog.vmId,
    reconnectionDialog.isConnected,
    getSession,
  ])

  const getIcon = useCallback((type) => {
    switch (type) {
      case GLOBALS.virtual_machine.types.W.id:
        return <AiFillWindows />
      case GLOBALS.virtual_machine.types.L.id:
        return <FcLinux />
      case GLOBALS.virtual_machine.types.O.id:
        return <MdDevices />
      case GLOBALS.virtual_machine.types.N.id:
        return <AiOutlineCluster />
      case GLOBALS.virtual_machine.types.T.id:
        return <AiOutlineCluster />
      case 'on':
        return <AiFillCheckCircle />
      case 'off':
        return <AiFillCloseCircle />
      case 'unknown':
        return <AiFillCloseCircle style={{ opacity: 0.5 }} />
      case 'error':
        return <AiFillCloseCircle style={{ color: 'red' }} />
      default:
        return <MdDevices />
    }
  }, [])

  // Función para detectar cambios en el modo pantalla completa
  const handleFullscreenChange = useCallback(() => {
    if (
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    ) {
      setIsFullscreen(true) // Ahora estamos en pantalla completa
    } else {
      setIsFullscreen(false) // Hemos salido de pantalla completa
    }
  }, [])

  useEffect(() => {
    // Añadir listener para detectar cuando entramos o salimos de pantalla completa
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    // Limpiar los listeners cuando el componente se desmonte
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [handleFullscreenChange])

  const handleTabSelect = useCallback(
    (index) => {
      setDisplayStatus(true)
      setSelectedIndex(index)

      // Verificar si la pestaña seleccionada es una VM y si está desconectada
      const vmTabs = vmIds.map(({ vmId }) => vmId)
      const guideTabOffset = guideTab ? 1 : 0
      const topologyTabOffset = topologyTab ? 1 : 0
      const vmTabIndex = index - guideTabOffset - topologyTabOffset

      if (vmTabIndex >= 0 && vmTabIndex < vmTabs.length) {
        const vmId = vmTabs[vmTabIndex]
        const session = getSession(vmId)
        const resource = ResoucesTokens[vmId]

        if (session && !session.isConnected && resource) {
          // Mostrar diálogo de reconexión
          setReconnectionDialog({
            visible: true,
            vmId: vmId,
            displayName: resource.displayName,
            isConnected: false,
          })
        }
      }
    },
    [vmIds, guideTab, topologyTab, getSession, ResoucesTokens]
  )

  const handleReconnect = useCallback(
    (vmId) => {
      const resource = ResoucesTokens[vmId]
      if (resource) {
        startSession(vmId, resource.token, resource, token)
      }
    },
    [ResoucesTokens, startSession]
  )

  const handleReconnectionClose = useCallback(() => {
    setReconnectionDialog({
      visible: false,
      vmId: null,
      displayName: '',
      isConnected: false,
    })
  }, [])

  const toggleFullscreen = () => {
    const elem = consoleRef.current // Referencia al contenedor de la consola
    if (elem) {
      if (!isFullscreen) {
        // Intentar entrar en pantalla completa
        if (elem.requestFullscreen) {
          elem.requestFullscreen().catch((err) => {
            console.error('Error al intentar entrar en pantalla completa: ', err)
          })
        } else if (elem.mozRequestFullScreen) {
          // Firefox
          elem.mozRequestFullScreen().catch((err) => {
            console.error('Error al intentar entrar en pantalla completa (Firefox): ', err)
          })
        } else if (elem.webkitRequestFullscreen) {
          // Chrome, Safari y Opera
          elem.webkitRequestFullscreen().catch((err) => {
            console.error('Error al intentar entrar en pantalla completa (Webkit): ', err)
          })
        } else if (elem.msRequestFullscreen) {
          // IE/Edge
          elem.msRequestFullscreen().catch((err) => {
            console.error('Error al intentar entrar en pantalla completa (IE/Edge): ', err)
          })
        }
      } else {
        // Intentar salir de pantalla completa
        if (
          document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        ) {
          if (document.exitFullscreen) {
            document.exitFullscreen().catch((err) => {
              console.error('Error al intentar salir de pantalla completa: ', err)
            })
          } else if (document.mozCancelFullScreen) {
            // Firefox
            document.mozCancelFullScreen().catch((err) => {
              console.error('Error al intentar salir de pantalla completa (Firefox): ', err)
            })
          } else if (document.webkitExitFullscreen) {
            // Chrome, Safari y Opera
            document.webkitExitFullscreen().catch((err) => {
              console.error('Error al intentar salir de pantalla completa (Webkit): ', err)
            })
          } else if (document.msExitFullscreen) {
            // IE/Edge
            document.msExitFullscreen().catch((err) => {
              console.error('Error al intentar salir de pantalla completa (IE/Edge): ', err)
            })
          }
        } else {
          console.error('El documento no está en modo pantalla completa, no se puede salir.')
        }
      }
    } else {
      console.error('consoleRef is null, unable to enter fullscreen.')
    }
  }

  const ControlOptions = ({ vmId, ResoucesTokens }) => {
    const resource = ResoucesTokens[vmId]
    const session = getSession(vmId)

    const getStatusColor = () => {
      if (session.isConnected) return 'green'
      if (session.isReconnecting) return 'yellow'
      return 'red' // Desconectado
    }

    const toggleMenu = () => {
      setShowMenu(!showMenu)
    }

    const [isConnecting, setIsConnecting] = useState(false)
    const connectClient = async () => {
      setIsConnecting(true)
      try {
        await startSession(vmId, resource.token, resource, token)
        setDisplayStatus(true)
      } catch (error) {
        console.error('❌ Error completo en connectClient:', error)
      } finally {
        setIsConnecting(false)
      }
    }

    const diconnectClient = () => {
      disconnectClient(vmId)
      setDisplayStatus(false)
    }

    const sendCtrlAltDelete = () => {
      if (session && session.client) {
        const client = session.client
        client.sendKeyEvent(1, 0xffe3) // Presionar Ctrl
        client.sendKeyEvent(1, 0xffe9) // Presionar Alt
        client.sendKeyEvent(1, 0xffff) // Presionar Suprimir (Delete)

        client.sendKeyEvent(0, 0xffff) // Liberar Suprimir
        client.sendKeyEvent(0, 0xffe9) // Liberar Alt
        client.sendKeyEvent(0, 0xffe3) // Liberar Ctrl
      }
    }

    const handlePowerOff = async () => {
      try {
        if (!resource || !resource.host || resource.load === undefined || resource.load === null) {
          alert('No se encontró información completa de la máquina (host o load faltantes).')
          return
        }

        setIsPowerOffLoading(true)
        setPowerOffVmName(resource.displayName)

        await cycleLoadPDU(
          {
            host: resource.host,
            username: 'ElviraAdmin',
            password: '3lvira_:D',
            port: 22,
            load: resource.load,
          },
          token
        )

        alert('La máquina se esta reiniciando')
      } catch (error) {
        console.error('❌ Error completo en handlePowerOff:', error)
        alert('Error al reiniciar la máquina: ' + error.message)
      } finally {
        setIsPowerOffLoading(false)
      }
    }

    const handleTogglePower = async () => {
      try {
        if (!resource || !resource.host || resource.load === undefined || resource.load === null) {
          alert('No se encontró información completa de la máquina (host o load faltantes).')
          return
        }

        setIsPowerOffLoading(true)
        setPowerOffVmName(resource.displayName)

        // Determinar el nuevo estado (toggle)
        const currentState = pduStates[vmId] || 'on'
        const newState = currentState === 'on' ? 'off' : 'on'

        await setStateLoadPDU(
          {
            host: resource.host,
            username: 'ElviraAdmin',
            password: '3lvira_:D',
            port: 22,
            load: resource.load,
            state: newState,
          },
          token
        )

        // Actualizar el estado local
        setVmPowerStates((prev) => ({
          ...prev,
          [vmId]: newState,
        }))

        // Actualizar también pduStates para mantener sincronización
        setPduStates((prev) => ({
          ...prev,
          [vmId]: newState,
        }))

        // const action = newState === 'on' ? 'encendida' : 'apagada'
        // alert(`La máquina ha sido ${action}`)
      } catch (error) {
        console.error('❌ Error completo en handleTogglePower:', error)
        alert('Error al cambiar el estado de la máquina: ' + error.message)
      } finally {
        setIsPowerOffLoading(false)
      }
    }

    const StatusIndicator = () => {
      if (session.statusClient === 'Inactivo') {
        return <div className="lineal-loading-idle"></div>
      }

      if (session.statusClient === 'Esperando') {
        return <div className="lineal-loading"></div>
      }

      if (session.statusClient === 'Conectando') {
        return <div className="lineal-loading-connecting"></div>
      }

      if (session.statusClient === 'Desconectando') {
        return <div className="lineal-loading-disconnecting"></div>
      }

      return (
        <>
          <div className="status-circle" style={{ backgroundColor: getStatusColor() }}></div>
          <span className="status-label" title={session.errorMessage}>
            {session.isConnected
              ? 'Conectado'
              : session.isReconnecting
                ? 'Reconectando'
                : 'Desconectado'}
          </span>
        </>
      )
    }

    const PduStatusIndicator = () => {
      const pduState = pduStates[vmId] || 'unknown'

      const getPduStatusColor = () => {
        switch (pduState) {
          case 'on':
            return '#28a745' // Verde
          case 'off':
            return '#dc3545' // Rojo
          case 'error':
            return '#ffc107' // Amarillo
          default:
            return '#6c757d' // Gris
        }
      }

      const getPduStatusText = () => {
        switch (pduState) {
          case 'on':
            return 'PDU: ON'
          case 'off':
            return 'PDU: OFF'
          case 'error':
            return 'PDU: ERROR'
          default:
            return 'PDU: ?'
        }
      }

      return (
        <div className="pdu-status-indicator" style={{ marginLeft: '10px' }}>
          <div className="pdu-status-circle" style={{ backgroundColor: getPduStatusColor() }}></div>
          <span className="pdu-status-label" style={{ fontSize: '12px' }}>
            {getPduStatusText()}
          </span>
        </div>
      )
    }

    return (
      <div className="top-bar">
        <div className="right-side">
          <div className="status-indicator">
            <PduStatusIndicator />
            <StatusIndicator />
          </div>

          <div className="config-button">
            <Button
              icon="pi pi-bars"
              className="p-button-rounded p-button-secondary"
              onClick={toggleMenu}
            />
            {showMenu && (
              <div className="dropdown-menu">
                <Button
                  disabled={session.isConnected || isConnecting}
                  type="button"
                  icon="pi pi-check"
                  className="p-button-success"
                  onClick={connectClient}
                  label="Conectar"
                />
                <Button
                  disabled={!session.isConnected}
                  type="button"
                  icon="pi pi-times"
                  className="p-button-danger"
                  label="Desconectar"
                  onClick={diconnectClient}
                />
                <Button
                  disabled={!session.isConnected || isConnecting}
                  type="button"
                  icon="pi pi-key"
                  className="p-button-warning"
                  label="Ctrl+Alt+Supr"
                  onClick={sendCtrlAltDelete}
                />
                <Button
                  type="button"
                  icon="pi pi-copy"
                  className="p-button-info"
                  label="Portapapeles"
                  onClick={() => {
                    setClipboardVmId(vmId)
                    setShowMenu(false)
                  }}
                />
                <Button
                  type="button"
                  icon="pi pi-external-link"
                  className="p-button-warning"
                  label="Pantalla Completa"
                  onClick={toggleFullscreen}
                />
                <Button
                  type="button"
                  icon="pi pi-power-off"
                  className="p-button-secondary"
                  label="Reiniciar"
                  onClick={handlePowerOff}
                />
                <Button
                  type="button"
                  icon={pduStates[vmId] === 'off' ? 'pi pi-power-off' : 'pi pi-power-off'}
                  className={pduStates[vmId] === 'off' ? 'p-button-success' : 'p-button-danger'}
                  label={pduStates[vmId] === 'off' ? 'Prender' : 'Apagar'}
                  onClick={handleTogglePower}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={consoleRef}>
      {vmIds.length > 0 ? (
        <Tabs selectedIndex={selectedIndex} onSelect={handleTabSelect}>
          <TabList>
            {guideTab && <Tab key="guideTab">Guia</Tab>}

            {topologyTab && <Tab key="topology">Topologia</Tab>}

            {vmIds.map(({ vmId, displayName, resourceType }) => (
              <Tab key={vmId}>
                {getIcon(resourceType)} {`${displayName}`}
              </Tab>
            ))}
          </TabList>

          {guideTab && (
            <TabPanel key="guideTab">
              <div className="custom-tab-content">{guideTab}</div>
            </TabPanel>
          )}

          {topologyTab && (
            <TabPanel key="topology">
              <div className="custom-tab-content">{topologyTab}</div>
            </TabPanel>
          )}

          {vmIds.map(({ vmId, resourceType, displayName }) => {
            return (
              <TabPanel key={vmId}>
                <div style={{ position: 'relative' }}>
                {clipboardVmId === vmId && (
                  <ClipboardPanel
                    displayName={displayName}
                    connected={!!getSession(vmId).isConnected}
                    remoteText={getRemoteClipboard(vmId)}
                    send={(text) => sendClipboard(vmId, text)}
                    onClose={() => setClipboardVmId(null)}
                  />
                )}
                <div className="control-options-bar">
                  <ControlOptions vmId={vmId} ResoucesTokens={ResoucesTokens} />
                </div>
                {isLoading ? (
                  <div className="loading-container">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
                  </div>
                ) : displayStatus ? (
                  <GuacamoleConsole
                    vmId={vmId}
                    resourceType={resourceType}
                    session={getSession(vmId)}
                    errorMessage={errorMessage}
                    onSyncClipboard={async () => {
                      // Clic en la consola (gesto del usuario): Chrome pide permiso aquí. Máximo cada 3 s, como SRA
                      const now = Date.now()
                      if (now - (lastSyncRef.current[vmId] || 0) < 3000) return
                      lastSyncRef.current[vmId] = now
                      if (!clipboardApiAvailable()) return
                      try {
                        const text = clampClipboard((await navigator.clipboard.readText()) || '')
                        // No devolverle a la máquina lo que ella misma copió
                        if (text && text !== getRemoteClipboard(vmId)) sendClipboard(vmId, text)
                      } catch (e) {
                        /* sin permiso: usar el panel Portapapeles */
                      }
                    }}
                  />
                ) : (
                  <>
                    {/* {errorMessage  && <div className='lineal-loading-disconnecting'>{errorMessage}</div>} */}
                    {errorMessage === 'desconectado' && (
                      <div className="errorMessageDisconnected">Desconectado</div>
                    )}
                  </>
                )}
                </div>
              </TabPanel>
            )
          })}
        </Tabs>
      ) : (
        <h1>-</h1>
      )}
      <Dialog
        visible={isPowerOffLoading}
        onHide={() => { }} // No permitir cerrar durante el proceso
        header="Cambiando estado de la máquina"
        modal={true}
        closable={false}
        style={{ width: '400px' }}
      >
        <div className="power-off-loading-content">
          <div className="loading-spinner">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '3em', color: '#007ad9' }}></i>
          </div>
          <div className="loading-text">
            <h3>Procesando {powerOffVmName}</h3>
            <p>Por favor, espera mientras se cambia el estado de la máquina virtual...</p>
            <p className="loading-note">Este proceso puede tomar varios segundos.</p>
          </div>
        </div>
      </Dialog>

      {/* Diálogo de reconexión automática */}
      <ReconnectionDialog
        visible={reconnectionDialog.visible}
        vmId={reconnectionDialog.vmId}
        displayName={reconnectionDialog.displayName}
        onReconnect={() => handleReconnect(reconnectionDialog.vmId)}
        onClose={handleReconnectionClose}
        isConnected={reconnectionDialog.isConnected}
      />
    </div>
  )
}

export default MultiSessionManager
